import _extends from "@babel/runtime/helpers/extends";
import { ComponentHeadingSource, getComponentHeadingSource } from '@wix/a11y-adapters';
import { getHTagNumber, HeadingStructureFailedReason, headingStructureRule, parseEntityItemId, RuleId } from '@wix/a11y-rules';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { convertElementStatusToBooleanStatus, RuleElementListHeader, useAutoScrolling, useDeepCompare, useElementToggle } from '@wix/rules-panel-ui';
import { produce } from 'immer';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { currentElementInfoAtom } from '../../../atoms/bi-atoms';
import { scrollerNodeRefAtom } from '../../../atoms/session-atoms';
import { BiCtaNames } from '../../../bi/types';
import { usePanelStateAtom } from '../../../hooks/rules-state';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useGenericElement } from '../../../hooks/use-generic-element';
import { PanoramaErrorNames, PanoramaTransactions } from '../../../panorama/constants';
import { usePanoramaFunctions } from '../../../panorama/use-panorama-functions';
import { headingStructureOnApply } from '../../../rule-mappers/page-level-rules/heading-structure-on-apply';
import { useRuleBase } from '../../../rule-mappers/shared';
import { useGlobalSectionState } from '../../../rule-mappers/shared/use-global-section-state';
import { getCompRefType } from '../../../utils/get-comp-ref-type';
import { isEditor3Host } from '../../../utils/get-editor-host';
import { showSaveErrorModal } from '../../modals/show-save-error-modal';
import { convertBooleanStatusToElementStatus, convertScanStatusToUiStatus } from '../alt-image-fixer/utils';
import { HeadingStructureRule } from './heading-structure-rule';
import { useHeadingStructureAnalytics } from './hooks/use-heading-structure-analytics';
import { getHeadingContent } from './utils/get-heading-content';
const ruleId = RuleId.HEADING_STRUCTURE;
const ruleChecker = headingStructureRule.checker;
export const HeadingStructureRuleFixer = _ref => {
  let {
    htmlStrings,
    failedHeadings,
    failedReasons,
    a11yWizardAdapter,
    editorHost,
    pageId,
    breakpointId
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    reportErrorPanorama,
    finishPanoramaTransaction,
    startPanoramaTransaction
  } = usePanoramaFunctions();
  const {
    getRuleState
  } = usePanelStateAtom();
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const {
    updateGlobalSectionStateIfNeeded,
    getGlobalSectionModifiedComponentIds
  } = useGlobalSectionState();
  const {
    collapseElement,
    openNextElement,
    toggleElement,
    selectedElementId
  } = useElementToggle();
  const setCurrentElementInfo = useSetAtom(currentElementInfoAtom);
  const biLogger = useBiLogger();
  const [showLoader, setShowLoader] = useState(false);
  const scrollerNodeRef = useAtomValue(scrollerNodeRefAtom);
  const {
    selectedItemContentRef,
    selectedItemHeaderRef
  } = useAutoScrolling(selectedElementId, scrollerNodeRef);
  const {
    isGenericElement
  } = useGenericElement({
    getComponentStyleSource: getComponentHeadingSource,
    pageId,
    unknownValue: ComponentHeadingSource.UNKNOWN
  });
  const isNoMainHeading = failedReasons.includes(HeadingStructureFailedReason.NoMainHeading);
  const isMultipleMainHeading = failedReasons.includes(HeadingStructureFailedReason.MultipleMainHeadings);
  const headingProps = useMemo(() => htmlStrings.map((_ref2, index) => {
    let {
      id,
      uiElementId,
      htmlString
    } = _ref2;
    const headingDefinition = getHTagNumber(htmlString);
    const inFailedElement = Boolean(failedHeadings.find(failedHeading => failedHeading.id === uiElementId));
    return {
      failedReason: isNoMainHeading && index === 0 ? HeadingStructureFailedReason.NoMainHeading : isMultipleMainHeading && headingDefinition === 1 ? HeadingStructureFailedReason.MultipleMainHeadings : inFailedElement ? HeadingStructureFailedReason.HeadingDefinition : undefined,
      headingContent: getHeadingContent(htmlString),
      headingDefinition: headingDefinition ? `H${headingDefinition}` : `p`,
      htmlString,
      id,
      status: convertBooleanStatusToElementStatus(!(inFailedElement || isNoMainHeading && index === 0 || isMultipleMainHeading && headingDefinition === 1)),
      uiElementId
    };
  }), [failedHeadings, htmlStrings, isMultipleMainHeading, isNoMainHeading]);
  const memoizedHeadingProps = useDeepCompare(headingProps);
  const [headingElements, setHeadingElements] = useState(memoizedHeadingProps);
  const {
    manualTaskDataEvent
  } = useHeadingStructureAnalytics({
    headingElements,
    pageId
  });
  const completedSize = useMemo(() => headingElements.filter(rule => convertElementStatusToBooleanStatus(rule.status)).length, [headingElements]);
  const total = useMemo(() => headingElements.length, [headingElements]);
  const openHeadingElement = useCallback(_ref3 => {
    let {
      id: uiElementId
    } = _ref3;
    const selectedElementIndex = memoizedHeadingProps.findIndex(el => el.uiElementId === uiElementId);
    setCurrentElementInfo({
      elementIndex: selectedElementIndex,
      elementStatus: convertElementStatusToBooleanStatus(memoizedHeadingProps[selectedElementIndex].status)
    });
    const {
      entities
    } = parseEntityItemId(uiElementId, 1);
    const compId = entities.length > 0 ? entities[0] : '';
    const nextCompRef = {
      id: compId,
      type: getCompRefType(editorHost, breakpointId)
    };
    if (nextCompRef) {
      a11yWizardAdapter.selectComponent(nextCompRef);
    }
  }, [a11yWizardAdapter, breakpointId, editorHost, memoizedHeadingProps, setCurrentElementInfo]);
  const handleCollapseAnimationEnd = useCallback(() => {
    setShowLoader(false);
    setHeadingElements(memoizedHeadingProps);
  }, [memoizedHeadingProps, setShowLoader, setHeadingElements]);
  useEffect(() => {
    manualTaskDataEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    openNextElement(memoizedHeadingProps.map(element => ({
      ...element,
      id: element.uiElementId
    })), openHeadingElement, handleCollapseAnimationEnd);
  }, [handleCollapseAnimationEnd, memoizedHeadingProps, openHeadingElement, openNextElement]);
  const handleClickApply = useCallback(index => async value => {
    const {
      htmlString,
      headingDefinition,
      id,
      uiElementId,
      status
    } = headingElements[index];
    startPanoramaTransaction(PanoramaTransactions.headingStructureRuleOnApply, {
      headingDefinition,
      htmlString,
      id,
      index,
      status,
      value
    });
    biLogger.taskElementCtaClick({
      ctaName: BiCtaNames.Apply,
      index,
      status
    });
    biLogger.taskElementApplyClick({
      index,
      status,
      value
    });
    setShowLoader(true);
    try {
      var _getRuleState;
      const newHeading = await headingStructureOnApply(value, headingDefinition, htmlString, id, uiElementId, a11yWizardAdapter, editorHost);
      const ruleState = (_getRuleState = getRuleState(pageId, ruleId)) == null ? void 0 : _getRuleState.state;
      const stateHtmlString = (ruleState == null ? void 0 : ruleState.htmlStrings) ?? htmlStrings;
      const oldHtmlIndex = stateHtmlString.findIndex(html => html.id === newHeading.id);
      if (oldHtmlIndex !== -1) {
        const updatedHtmlStrings = produce(stateHtmlString, draft => {
          draft[oldHtmlIndex] = newHeading;
        });
        const updatedGlobalSectionDataMap = updateGlobalSectionStateIfNeeded(new Map([[newHeading.id, {
          componentId: newHeading.id,
          htmlString: newHeading.htmlString
        }]]));
        updateRuleStateAndVerifyIfNeeded(pageId, ruleId, {
          htmlStrings: updatedHtmlStrings,
          status: convertScanStatusToUiStatus(ruleChecker({
            htmlStrings: updatedHtmlStrings
          }).status)
        }, getGlobalSectionModifiedComponentIds(updatedGlobalSectionDataMap));
      }
    } catch (error) {
      reportErrorPanorama(error, PanoramaErrorNames.headingStructureOnApply);
      console.error('Failed to update heading structure', error);
      await showSaveErrorModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleTryAgain: () => {
          handleClickApply(index)(value);
        },
        t
      });
      setShowLoader(false);
      return;
    }
    setIsApplyTriggered(true);
    finishPanoramaTransaction(PanoramaTransactions.headingStructureRuleOnApply, {
      headingDefinition,
      htmlString,
      id,
      index,
      status,
      value
    });
    setShowLoader(false);
  }, [headingElements, startPanoramaTransaction, biLogger, setIsApplyTriggered, finishPanoramaTransaction, a11yWizardAdapter, getRuleState, pageId, htmlStrings, updateGlobalSectionStateIfNeeded, updateRuleStateAndVerifyIfNeeded, getGlobalSectionModifiedComponentIds, reportErrorPanorama, editorHost, t]);
  const handleClickCancel = useCallback(index => () => {
    biLogger.taskElementCtaClick({
      ctaName: BiCtaNames.Cancel,
      index,
      status: headingElements[index].status
    });
    collapseElement();
  }, [biLogger, collapseElement, headingElements]);
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2"
  }, /*#__PURE__*/React.createElement(RuleElementListHeader, {
    completedSize: completedSize,
    title: t('accessibility-wizard.rules-list.heading-definition.list.failed-elements-number.heading'),
    totalSize: total
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "9px"
  }, headingElements.map((ruleProps, index) => {
    const {
      id,
      uiElementId
    } = ruleProps;
    const compRef = {
      id,
      type: getCompRefType(editorHost, breakpointId)
    };
    const onOpenElement = () => a11yWizardAdapter.selectComponent(compRef);
    const isGeneric = isEditor3Host(editorHost) ? !(a11yWizardAdapter.isBuilderRichTextComponent != null && a11yWizardAdapter.isBuilderRichTextComponent(compRef)) && isGenericElement(id) : isGenericElement(id);
    return /*#__PURE__*/React.createElement(HeadingStructureRule, _extends({
      isGeneric: isGeneric,
      key: uiElementId,
      pageId: pageId
    }, ruleProps, {
      contentRef: selectedItemContentRef,
      elementIndex: index,
      headerRef: selectedItemHeaderRef,
      isOpen: selectedElementId === uiElementId,
      showLoader: showLoader,
      toggleElement: toggleElement(uiElementId, onOpenElement),
      onClickApply: handleClickApply(index),
      onClickCancel: handleClickCancel(index)
    }));
  })));
};
//# sourceMappingURL=heading-structure-rule-fixer.js.map